/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  CHANNEL_FIELD,
  COLOR_MODE,
  DataMessage,
  TypedArray,
} from '@ouster/webviz';
import { PointCloudSensorsSettings } from '@ouster/webviz-react-components';

import { MAX_RETURNS } from './constants';

// Re-exported rather than redeclared: these now live alongside
// hasCalibratedReflectivity in @ouster/webviz-react-components.
export type {
  Reflectivity,
  CalibrationStatus,
} from '@ouster/webviz-react-components';

// TODO: swap record string type for LIDAR_PROFILE
export const PROFILES: Record<string, Record<string, TypedFun>> = {
  LEGACY: {
    RANGE: Uint32Array,
    SIGNAL: Uint32Array,
    NEAR_IR: Uint32Array,
    REFLECTIVITY: Uint32Array,
    RGB: Float64Array,
  },
  RNG19_RFL8_SIG16_NIR16: {
    RANGE: Uint32Array,
    SIGNAL: Uint16Array,
    NEAR_IR: Uint16Array,
    REFLECTIVITY: Uint16Array, // Reflectivity can be greater than 255 (uncalibrated, etc) so SDK uses Uint16 for some buffer
    RGB: Float64Array,
  },
  RNG19_RFL8_SIG16_NIR16_DUAL: {
    RANGE: Uint32Array,
    RANGE2: Uint32Array,
    REFLECTIVITY: Uint8Array,
    REFLECTIVITY2: Uint8Array,
    SIGNAL: Uint16Array,
    SIGNAL2: Uint16Array,
    NEAR_IR: Uint16Array,
    RGB: Float64Array,
  },
  // Low data rate
  // SDK lidar scan impl uses uint16 for reflectivity and nir, so we use them here as well.
  RNG15_RFL8_NIR8: {
    RANGE: Uint32Array,
    REFLECTIVITY: Uint16Array, // Should be 8 bit, unclear why it's 16 but matching SDK for now.
    NEAR_IR: Uint16Array, // Is an 8 bit field but gets left shifted by 4 bits
    RGB: Float64Array,
  },
  FUSA_RNG15_RFL8_NIR8_DUAL: {
    RANGE: Uint32Array,
    RANGE2: Uint32Array,
    REFLECTIVITY: Uint8Array,
    REFLECTIVITY2: Uint8Array,
    NEAR_IR: Uint8Array,
    RGB: Float64Array,
  },
};

export const debounce = (fn: (...args: any[]) => void, delay: number) => {
  let timerId: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * TODO (mw): Copied from studio. Move out to webviz lib
 * Create a duplicate of a data message, so that its fields
 * can be modified independently.
 * @param src - Source data message
 * @returns Deep copy of data message
 */
export function copyDataMessage(src: DataMessage): DataMessage {
  const {
    sensorInfo,
    availableChannels,
    timeCreated,
    timestamps,
    rgbChannels,
  } = src;
  const channelFieldCopy: Record<string, TypedArray> = {};
  src.availableChannels.forEach((channelName) => {
    const arrToCp = src.channelFields[channelName];
    channelFieldCopy[channelName] =
      rgbChannels && rgbChannels.includes(channelName)
        ? arrToCp
        : arrToCp.slice(0);
  });
  const dmCopy: DataMessage = {
    sensorInfo,
    availableChannels,
    channelFields: channelFieldCopy,
    timestamps,
    timeCreated,
    rgbChannels,
  };
  if (src.fileProgress) {
    dmCopy.fileProgress = src.fileProgress;
  }
  if (src.measurementIds) {
    dmCopy.measurementIds = src.measurementIds;
  }
  return dmCopy;
}

export const getAvailableReturns = ({
  colorMode,
  actualChannels,
}: {
  colorMode: string;
  actualChannels?: (CHANNEL_FIELD | string)[];
}) => {
  if (!actualChannels || actualChannels.length === 0) {
    return [];
  }
  const hasDualRange = actualChannels.includes('RANGE2');

  const availableReturns = Array.from(Array(MAX_RETURNS).keys()).filter(
    (returnNumber) => {
      const channelKey = `${colorMode}${
        returnNumber === 0 ? '' : returnNumber + 1
      }`;
      return actualChannels.includes(channelKey) || hasDualRange;
    }
  );

  return availableReturns;
};

export type TypedFun =
  | Uint32ArrayConstructor
  | Uint16ArrayConstructor
  | Uint8ArrayConstructor
  | Float32ArrayConstructor
  | Float64ArrayConstructor;

export enum LIDAR_PROFILE {
  LEGACY = 'LEGACY',
  SINGLE_RETURN = 'RNG19_RFL8_SIG16_NIR16',
  DUAL_RETURN = 'RNG19_RFL8_SIG16_NIR16_DUAL',
  LOW_DATA_RATE = 'RNG15_RFL8_NIR8',
  FUSA = 'FUSA_RNG15_RFL8_NIR8_DUAL',
}

/**
 * Function to infer point cloud sensors settings state object from the data's lidar profile
 * and selected coloring mode.
 * We need coloring mode (channel field) to infer how many returns there are for the
 * channel field. Then, we filter from all possible pointcloud sensor settings to
 * those that are visible and available.
 * Default to reflectivity color mode if missing
 *
 * This is a duplicate of the function present in Ouster Studio Desktop.
 * @param profile
 * @param coloringMode
 * @param sn
 * @returns
 */
export const getPointCloudSensorsSettings = (
  coloringMode?: COLOR_MODE | string,
  sn?: string,
  actualChannels?: (CHANNEL_FIELD | string)[]
): PointCloudSensorsSettings => {
  // Explicit return type
  if (!sn) {
    // If sn is not provided, return an empty object conforming to the type
    return {};
  }

  const availableReturns = getAvailableReturns({
    colorMode: coloringMode ?? COLOR_MODE.REFLECTIVITY,
    actualChannels,
  });

  // Initialize the settings structure directly for the given sn
  const initialSettings = {
    returnsSettings: availableReturns.map(() => ({
      visible: true,
      highlightColor: null,
    })),
    sensorVisibility: true,
  };

  // Apply the visibility/availability logic
  const visibleReturnNumbers = initialSettings.returnsSettings
    .slice(1) // Check returns other than the first
    .filter(({ visible }) => visible);
  const visibleAndAvailableReturnNumbers = visibleReturnNumbers.findIndex(
    (_, returnNumber) => availableReturns.includes(returnNumber + 1) // +1 because availableReturns is 0-indexed, but we compare against return number (1st, 2nd, etc.)
  );

  // If other returns are marked visible but aren't available for the current mode, force the first return visible.
  const isForceFirstReturn =
    visibleReturnNumbers.length > 0 && visibleAndAvailableReturnNumbers < 0;

  const finalReturnSettings = availableReturns.map((returnNumber) => {
    const returnSettings = initialSettings.returnsSettings[returnNumber];
    return {
      ...(returnSettings ?? { visible: true, highlightColor: null }), // Provide default if somehow missing
      disabled: false, // Mark as not disabled
      visible:
        isForceFirstReturn && returnNumber === 0
          ? true // Force first return visible if needed
          : returnSettings?.visible ?? true, // Otherwise, use existing visibility or default to true
    };
  });

  // Construct the final object with the SN key
  const pointCloudSensorsSettings: PointCloudSensorsSettings = {
    [sn]: {
      returnsSettings: finalReturnSettings,
      sensorVisibility: true,
    },
  };

  return pointCloudSensorsSettings;
};

/**
 * Helper to extract lidar profile from metadata object if possible.
 * Looks in config params, but config params is missing in legacy metadata format,
 * so use data format if config params is missing.
 * Substitutes legacy profile if missing.
 * @param metadata
 */
export const extractLidarProfileFromMetadata = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: any
): LIDAR_PROFILE => {
  const { configParams, dataFormat } = metadata ?? {};
  if (configParams?.udpProfileLidar) {
    if (Object.values(LIDAR_PROFILE).includes(configParams.udpProfileLidar)) {
      return configParams.udpProfileLidar;
    }
    console.debug(
      `Did not recognize lidar profile: ${configParams.udpProfileLidar}!`
    );
    return LIDAR_PROFILE.SINGLE_RETURN;
  }
  if (dataFormat?.udpProfileLidar) {
    if (Object.values(LIDAR_PROFILE).includes(dataFormat.udpProfileLidar)) {
      return dataFormat.udpProfileLidar;
    }
    console.debug(
      `Did not recognize lidar profile ${dataFormat.udpProfileLidar}!`
    );
    return LIDAR_PROFILE.SINGLE_RETURN;
  }
  console.warn(
    `Was unable to find udp_profile_lidar in metadata! Using SINGLE_RETURN as a fallback.`
  );
  return LIDAR_PROFILE.SINGLE_RETURN;
};

/**
  Format tag user created according to backend requirement.
  Only allow dashes and underscores as special characters 
 * @param tagName
 */
export const formatTag = (tagName: string) => {
  // Only allow lowercase
  let suggestedReplacement = tagName.trim().toLowerCase();
  // Spaces should be underscores (only one underscore between words)
  suggestedReplacement = suggestedReplacement.replace(/\s+/g, '_');
  // Only allow dashes and underscores as special characters
  suggestedReplacement = suggestedReplacement.replace(/[^a-z0-9-_]+/g, '');

  return suggestedReplacement;
};

/* 
    react-select has special requirement for its option, it needs label and value
    hence, we have a mapping function here to give the name and value its needed
    at the same time, pass our category/tag as data value
  */
export const mapData = <T,>(data: T[]) => {
  const mappedData = data.map((item) => ({
    label: (item as any).name,
    value: (item as any).name,
    data: item,
  }));

  return mappedData;
};

/* 
  Custom function to format bytes to GB for better display
*/
export const formatBytesToGB = (bytes: number) => {
  const gb = bytes / 1000 / 1000 / 1000;
  const roundedGb = Math.round(gb * 100) / 100; // Round to two decimal places
  return roundedGb % 1 === 0
    ? `${roundedGb.toFixed(0)} GB`
    : `${roundedGb.toFixed(2)} GB`;
};

/* 
  Custom function to format bytes in MB, GB, TB PB and EB, for better display
*/
export const formatBytes = (bytes: number | undefined) => {
  if (bytes === undefined) return 'N/A';
  const units = ['GB', 'TB', 'PB', 'EB'];
  let size = bytes / 1000 / 1000 / 1000; // Convert to MB
  let unitIndex = 0;

  while (size >= 1000 && unitIndex < units.length - 1) {
    size /= 1000;
    unitIndex += 1;
  }

  const roundedSize = Math.round(size * 100) / 100; // Round to two decimals
  return roundedSize % 1 === 0
    ? `${roundedSize.toFixed(0)} ${units[unitIndex]}`
    : `${roundedSize.toFixed(2)} ${units[unitIndex]}`;
};

/* 
  Helper to get chart color based on space usage
*/
export const getUsageColor = (used: number): string => {
  if (used < 50) return 'var(--ouster-colors-green-400)';
  if (used < 90) return 'var(--ouster-colors-yellow-300)';
  return 'var(--ouster-colors-red-100)';
};

/* 
  Helper to format large number in a compact mode with 2 digit
*/
export const formatNumber = (num: number | undefined): string => {
  if (num === undefined) return 'N/A';

  if (num < 1000) {
    return num.toString(); // Show whole number as-is if below 1000
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(num);
};

export const formatUnitTimestamp = (timestamp: number): string => {
  if (timestamp === 0) return 'Long time ago';
  const ts = new Date(timestamp);

  return ts.toISOString().split('T')[0];
};
